import { Injectable } from '@angular/core';
import { MigrationResult } from '../migration-result/migration-result';
import { MigrationSource } from '../migration-reusable-components/migration-source.enum';
import { ProductMigration } from '../migration-side-nav/product-migration';

/**
 * Is not same as MigrationStatus. This one expected to be used only for this service.
 */
export enum MigrationStatusType {
	IN_PROGRESS, // IN_PROGRESS includes migration statuses such as NEW, INPROGRESS, INQUEUE, PENDINGCONFLICTS
	COMPLETED
}

/**
 * Service to cache results by migration source and status.
 */
@Injectable({
	providedIn: 'root'
})
export class MigrationDetailsService {

	productMigrationStateMap = new Map<MigrationSource, ProductMigration>();

	/**
	 * Each product will have it's own IN-PROGRESS/NEW, COMPLETED and PAUSED migration result
	 * Map for IN-PROGRESS/NEW, COMPLETED and PAUSED
	 */
	private migrationResultMap = new Map<MigrationSource, Map<MigrationStatusType, Array<MigrationResult>>>();

	constructor() { }

	/**
	 * Cache migrationResults by migrationSource and migrationStatus.
	 */
	cacheMigrationResults(
		migrationSource: MigrationSource,
		migrationStatus: MigrationStatusType,
		newMigrationResult: Array<MigrationResult>
	): void {
		this.addToProductMapByMigrationSource(migrationSource, migrationStatus, newMigrationResult);
	}

	/**
	 * Additionally cache by 'ALL' migrationSource results, so when user navigates back to migration dashboard tab
	 * the result from other becomes visible.
	 */
	cacheAdditionallyMigrationResults(
		migrationSource: MigrationSource,
		migrationStatus: MigrationStatusType,
		newMigrationResult: Array<MigrationResult>
	): void {
		if (migrationSource !== MigrationSource.ALL) {
			this.addAdditionallyToProductMapByMigrationSourceALL(migrationStatus, newMigrationResult);
		}
		this.addToProductMapByMigrationSource(migrationSource, migrationStatus, newMigrationResult);
	}

	private addAdditionallyToProductMapByMigrationSourceALL(migrationStatus: MigrationStatusType, newMigrationResults: MigrationResult[]) {
		const productMigrationResults = this.migrationResultMap.get(MigrationSource.ALL);
		if (productMigrationResults) { // this product map is already there
			const existingMigrationResults = productMigrationResults.get(migrationStatus);
			if (existingMigrationResults) {
				productMigrationResults.set(migrationStatus, existingMigrationResults.concat(newMigrationResults));
			} else {
				productMigrationResults.set(migrationStatus, newMigrationResults);
			}
		} else {
			const newMigrationResultMap = new Map<MigrationStatusType, Array<MigrationResult>>();
			newMigrationResultMap.set(migrationStatus, newMigrationResults);
			this.migrationResultMap.set(MigrationSource.ALL, newMigrationResultMap);
		}
	}

	private addToProductMapByMigrationSource(
		migrationSource: MigrationSource, migrationStatus: MigrationStatusType,
		newMigrationResult: Array<MigrationResult>): void {
		const productMigrationResults = this.migrationResultMap.get(migrationSource);
		if (productMigrationResults) { // this product map is already there
			productMigrationResults.set(migrationStatus, newMigrationResult);
		} else {
			const newMigrationResultMap = new Map<MigrationStatusType, Array<MigrationResult>>();
			newMigrationResultMap.set(migrationStatus, newMigrationResult);
			this.migrationResultMap.set(migrationSource, newMigrationResultMap);
		}
	}

	/**
	 * @return cached migration results filtered by migrationSource and migrationStatus. Empty array is returned if results
	 * are not found.
	 * Results cached by migration source 'ALL' are not going be cached by other migration source. So if user navigates
	 * from migration dashboard tab than the other tab should not displayed migration results cached for migration dashboard tab.
	 */
	getCachedMigrationResults(migrationSource: MigrationSource, migrationStatus: MigrationStatusType): Array<MigrationResult> {
		let results: Array<MigrationResult> = [];
		const productMigrationResults = this.migrationResultMap.get(migrationSource);
		if (productMigrationResults) { // this product map is already there
			results = productMigrationResults.get(migrationStatus);
		}
		return results;
	}
}
